import { useMemo } from 'react';
import { Stack } from '@mui/material';
import { keyframes } from '@mui/material/styles';

import { Box, Typography } from '@occc-app/design-system';

import type { MessageComponentProps } from './message-types.types';

import { TEXT_WRAP_SX } from '../../../../drive-insights.constants';
import { ResponseContent } from '../../response-content.component';
import { ReactionButtons } from '../../reaction-buttons.component';
import { extractReadableText } from '../../../utils/extract-readable-text';
import { ThinkingIndicator } from '../../thinking-indicator.component';

const responseTextSx = {
  fontSize: 15,
  lineHeight: '26px',
  color: 'drive.textPrimary',
};

const slideReveal = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 50px;
    opacity: 1;
  }
`;

const thinkingRevealSx = {
  overflow: 'hidden',
  animation: `${slideReveal} 0.3s ease-out`,
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
};

export const ResponseMessage = ({
  message,
  onReactionChange,
  streamingStatusText,
}: MessageComponentProps) => {
  const hasContent = message.content || (message.parsedContent && message.parsedContent.length > 0);

  const content = useMemo(() => {
    // ThinkingIndicator is rendered below during streaming; return null to avoid duplicate
    if (message.isStreaming && !hasContent) {
      return null;
    }

    // Render parsed content if available
    if (message.parsedContent && message.parsedContent.length > 0) {
      return (
        <ResponseContent parsedContent={message.parsedContent} isStreaming={message.isStreaming} />
      );
    }

    // Fallback to raw content
    return <Typography sx={TEXT_WRAP_SX}>{message.content}</Typography>;
  }, [message.isStreaming, message.parsedContent, message.content, hasContent]);

  const copyContent = useMemo(() => {
    if (message.parsedContent && message.parsedContent.length > 0) {
      return extractReadableText(message.parsedContent);
    }
    return message.content;
  }, [message.parsedContent, message.content]);

  return (
    <Box sx={{ width: '100%', minWidth: 0, ...responseTextSx }}>
      <Stack spacing={1.5}>
        {!message.isStreaming && message.thinkingEndedAt && (
          <Box sx={thinkingRevealSx}>
            <ThinkingIndicator
              thinkingStartedAt={message.thinkingStartedAt}
              thinkingEndedAt={message.thinkingEndedAt}
            />
          </Box>
        )}
        {content}
        {message.isStreaming && (
          <ThinkingIndicator
            thinkingStartedAt={message.thinkingStartedAt}
            statusText={streamingStatusText}
          />
        )}
      </Stack>
      {onReactionChange && !message.isStreaming && (
        <ReactionButtons
          responseId={message.id}
          content={copyContent}
          currentReaction={message.reaction}
          onReactionChange={onReactionChange}
        />
      )}
    </Box>
  );
};
